/*
 * File: CommentCreator.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: July 30, 2025 at 2:05 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import validator from 'validator';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// Utilities
import { useWindowResize } from 'beautiful-react-hooks';
import { containsUrl } from '../../../utilities/utilities';
import { toastMessage, toastSuccess } from '../../utilities/toaster';

// Services
import { postComment, validateComment } from '../../services/comment';

// Components
import { TextInput } from '../TextInput';
import { TextArea } from '../TextArea';
import { Button } from '../Button';
import { IconElement } from '../IconElement';
import { Typography } from '../Typography';
import { CommentDiscardConfirmationModal } from '../CommentDiscardConfirmationModal';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SOURCES } from '../../../constants/base.constants';

// Styles
import * as S from './CommentCreator.styles';


/**
 * Component
 */

export const CommentCreator = memo(({
	className, initialCompact, source, sourceId, replyId, onClose, postCreateActions
}) => {

	// Create state handlers
	const [comment, setComment] = useState(null);
	const [isCompact, setIsCompact] = useState(initialCompact);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [alertText, setAlertText] = useState(null);
	const [alertHeight, setAlertHeight] = useState(40);
	const [showAlert, setShowAlert] = useState(false);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Create references
	const inputRef = useRef();
	const alertContainerRef = useRef();

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get actions from hooks
	const { executeRecaptcha } = useGoogleReCaptcha();

	// Handle on input change action
	const handleOnChange = (event) => {
		const { value } = event.target;
		setComment(value);
	};

	// Handle validate comment
	const validateCommentContent = async () => {

		// Validate parameters
		if (!comment || validator.isEmpty(comment, { ignore_whitespace: true })) {

			// Show error
			toastMessage(uiMode, partner, t('commentCreator.missingCommentError'));

			// Return
			return false;
		}

		// Check comment content for web links
		if (containsUrl(comment, [
			'www.ourwave.org',
			'stories.ourwave.org',
			'community.ourwave.org',
			'discover.ourwave.org',
			partner.full_domain
		].filter(Boolean))) {

			// Show alert
			setAlertText(t('commentCreator.webLinksError', { partnerName: partner.name }));
			setShowAlert(true);

			// Return
			return false;
		}

		// Update submission state
		setIsSubmitting(true);

		// Update alert state
		setShowAlert(false);

		// Pre-review comment
		const { data: { result: { validation_error: validationError } } } = await validateComment({ content: comment });

		// Show error if necessary
		if (validationError) {

			// Show alert
			setAlertText(t(`validationErrors.${validationError}_comment`, { partnerName: partner.name }));
			setShowAlert(true);

			// Update submission state
			setIsSubmitting(false);

			// Return
			return false;
		}

		// Return true
		return true;
	};

	// Handle submit comment
	const submitComment = async () => {

		// Trigger GTM event
		try {
			window.dataLayer.push({ event: 'submit_comment_step_1' });
		} catch (err) {}

		// Validate comment
		const result = await validateCommentContent(true);

		// Return if necessary
		if (!result) return;

		// Update alert state
		setShowAlert(false);

		// Create parameters
		const params = {
			content: comment,
			source,
			sourceId,
			...replyId ? {
				replyCommentId: replyId
			} : undefined
		};

		// Add reCAPTCHA token
		try { params.recaptchaToken = await executeRecaptcha('post_comment'); } catch (_) {}

		// Attempt comment submission
		try {

			// Post comment
			const { data: { result: { comment: commentObj } } } = await postComment(params);

			// Update submission state
			setIsSubmitting(false);

			// Trigger GTM event
			try {
				window.dataLayer.push({ event: 'submit_comment_step_2' });
			} catch (err) {}

			// Update state
			setIsCompact(true);

			// Clear content
			setComment(null);

			// Reset alert
			setShowAlert(false);

			// Show success message
			toastSuccess(uiMode, partner, t('commentCreator.confirmationMessage', { partnerName: partner.name }));

			// Handle close function
			if (onClose) onClose();

			// Handle post create actions
			if (postCreateActions) postCreateActions(commentObj);

		} catch (error) {

			// Update submission state
			setIsSubmitting(false);

			// Get parameters
			const isValidationError = error?.response?.data?.error?.is_validation_error;

			// Check if validation error
			if (isValidationError) {

				// Get validation error
				const validationError = error?.response?.data?.error?.validation_error;

				// Show alert
				setAlertText(t(`validationErrors.${validationError}_comment`, { partnerName: partner.name }));
				setShowAlert(true);

			} else {

				// Show error
				toastMessage(uiMode, partner, t('commentCreator.submissionError'));
			}
		}
	};

	// Handle expand creator
	const expandCreator = () => {

		// Update state
		setIsCompact(false);

		// Clear content
		setComment(null);

		// Set timeout
		setTimeout(() => {

			// Focus input
			inputRef.current?.focus();

		}, 100);

		// Reset alert
		setShowAlert(false);
	};

	// Handle confirm cancel comment
	const cancelComment = () => {

		// Update state
		setShowConfirmationModal(false);
		setIsCompact(true);

		// Clear content
		setComment(null);

		// Reset alert
		setShowAlert(false);

		// Handle close function
		if (onClose) onClose();
	};

	// Handle confirm cancel comment
	const confirmCancelComment = () => {

		// Cancel if should show modal
		if (comment) {

			// Update state
			setShowConfirmationModal(true);

		} else {

			// Cancel comment creation
			cancelComment();
		}
	};

	// Handle actions on compact change
	useEffect(() => {

		// Set element heights
		setAlertHeight(alertContainerRef?.current?.clientHeight);

	}, []);

	// Handle actions on window resize
	useWindowResize(() => {

		// Set element heights
		setAlertHeight(alertContainerRef?.current?.clientHeight);
	});

	// Handle actions on alert text change
	useEffect(() => {

		// Set element height
		setAlertHeight(alertContainerRef?.current?.clientHeight);

	}, [alertText]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper className={className}>
			{isCompact ? (
				<TextInput
					placeholder={t('commentCreator.placeholder')}
					name="comment"
					type="text"
					value=""
					onFocus={expandCreator}
				/>
			) : (
				<S.CreatorContainer>

					{/* Creator */}
					<TextArea
						name="comment"
						type="text"
						rows={1}
						characterLimit={500}
						defaultHideCharacterLimit
						value={comment || ''}
						onChange={handleOnChange}
						smartField
						ref={inputRef}
						size="small"
						autoResize
					/>

					{/* Actions */}
					<S.Actions>

						{/* Spacer */}
						<S.Spacer />

						{/* Submit Button */}
						<Button
							variation="secondary"
							size="medium"
							disabled={isSubmitting}
							onClick={confirmCancelComment}
						>
							<Trans t={t} i18n="actions.cancel">
								Cancel
							</Trans>
						</Button>

						{/* Post Button */}
						<Button
							onClick={submitComment}
							size="medium"
							disabled={isSubmitting}
							isLoading={isSubmitting}
						>
							<Trans t={t} i18n="actions.post">
								Post
							</Trans>
						</Button>
					</S.Actions>

				</S.CreatorContainer>
			)}

			{/* Alert Container */}
			<S.AlertContainer className={showAlert ? 'animate show' : 'animate'} ref={alertContainerRef} $height={alertHeight}>
				<IconElement icon={['fad', 'triangle-exclamation']} />
				<Typography tag="p" variation="3" weight="medium">{alertText}</Typography>
			</S.AlertContainer>

			{/* Modals */}
			<CommentDiscardConfirmationModal
				isOpen={showConfirmationModal}
				handleCancel={() => {
					setShowConfirmationModal(false);
				}}
				handleConfirm={cancelComment}
			/>

		</S.Wrapper>
	);
});


/**
 * Configuration
 */

CommentCreator.displayName = 'CommentCreator';
CommentCreator.propTypes = {
	className: PropTypes.string,
	initialCompact: PropTypes.bool,
	source: PropTypes.oneOf([SOURCES.STORY, SOURCES.QUESTION, SOURCES.MESSAGE]),
	sourceId: PropTypes.string,
	replyId: PropTypes.string,
	onClose: PropTypes.func,
	postCreateActions: PropTypes.func
};
CommentCreator.defaultProps = {
	className: null,
	initialCompact: true,
	source: null,
	sourceId: null,
	replyId: null,
	onClose: null,
	postCreateActions: null
};
